import { useEffect, useMemo, useState } from "react";
import { isValidPhoneNumber, type CountryCode } from "libphonenumber-js/min";
import { COUNTRIES, COUNTRIES_BY_DIAL_LENGTH } from "./countries";
import { CountryPicker } from "./CountryPicker";
import { Input } from "@/components/ui/input";

/**
 * Two-way phone input: the country dropdown and the "+dial national" text
 * field stay in lock-step.
 *
 *  - Selecting a country writes its dial code (e.g. "+30 ") into the input.
 *  - Typing/pasting a "+X..." prefix into the input matches it against the
 *    longest-dial-first country table and updates the dropdown accordingly.
 *  - Editing only the national part keeps the currently-selected country.
 *  - Backspace over the whole field simply blanks it — dropdown stays put.
 */
export function PhoneInput({
  value,
  onChange,
  defaultDialIso2 = "GR",
  required = false,
  disabled = false,
  inputId,
}: {
  value: string | null;
  onChange: (e164OrNull: string | null) => void;
  defaultDialIso2?: string;
  required?: boolean;
  disabled?: boolean;
  inputId?: string;
}) {
  const initial = useMemo(() => {
    if (!value) {
      const defCountry = COUNTRIES.find((c) => c.iso2 === defaultDialIso2) ?? COUNTRIES[0]!;
      return { iso2: defCountry.iso2, dial: defCountry.dialCode, national: "" };
    }
    for (const c of COUNTRIES_BY_DIAL_LENGTH) {
      if (value.startsWith(c.dialCode)) {
        return { iso2: c.iso2, dial: c.dialCode, national: value.slice(c.dialCode.length) };
      }
    }
    const def = COUNTRIES.find((c) => c.iso2 === defaultDialIso2) ?? COUNTRIES[0]!;
    return { iso2: def.iso2, dial: def.dialCode, national: value.replace(/^\+/, "") };
  }, [value, defaultDialIso2]);

  const [iso2, setIso2] = useState(initial.iso2);
  // `raw` is what the user sees in the field — invariant: normally starts
  // with the currently-selected country's dial code + space, but during
  // mid-type of a new "+xxx" prefix it can hold the in-flight string.
  const [raw, setRaw] = useState(() => formatRaw(initial.dial, initial.national));
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setIso2(initial.iso2);
    setRaw(formatRaw(initial.dial, initial.national));
    setTouched(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function emit(nextIso2: string, nationalDigits: string) {
    if (!nationalDigits) { onChange(null); return; }
    const dial = COUNTRIES.find((c) => c.iso2 === nextIso2)?.dialCode ?? "";
    onChange(`${dial}${nationalDigits}`);
  }

  function handleCountryChange(newIso2: string) {
    const newDial = COUNTRIES.find((c) => c.iso2 === newIso2)?.dialCode ?? "";
    // Preserve the national digits the user has already typed for the new
    // country — strip the OLD dial code if the raw was in canonical shape,
    // otherwise take all digits (fall-back for mid-typed rare states).
    const oldDial = COUNTRIES.find((c) => c.iso2 === iso2)?.dialCode ?? "";
    const trimmed = raw.trimStart();
    const nationalPart = trimmed.startsWith(oldDial)
      ? trimmed.slice(oldDial.length).trim()
      : trimmed.replace(/^\+\d+\s*/, "").trim(); // strip any leading +dial
    const nationalDigits = nationalPart.replace(/\D/g, "");
    setIso2(newIso2);
    setRaw(formatRaw(newDial, nationalPart));
    emit(newIso2, nationalDigits);
  }

  function handleRawChange(next: string) {
    // Allow +, digits, spaces, dashes, parens. Everything else is dropped —
    // no letters, no punctuation surprises from mobile keyboards.
    const cleaned = next.replace(/[^\d+ \-()]/g, "");
    setRaw(cleaned);

    if (cleaned.trim().startsWith("+")) {
      // Try to match the longest dial code — that's why COUNTRIES_BY_DIAL_LENGTH
      // is sorted longest-first (so "+1246" beats "+1").
      for (const c of COUNTRIES_BY_DIAL_LENGTH) {
        if (cleaned.startsWith(c.dialCode)) {
          const rest = cleaned.slice(c.dialCode.length).trimStart();
          const nationalDigits = rest.replace(/\D/g, "");
          if (iso2 !== c.iso2) setIso2(c.iso2);
          emit(c.iso2, nationalDigits);
          return;
        }
      }
      // Mid-typed prefix that hasn't matched yet (e.g. just "+" or "+3") —
      // don't emit an E.164; the parent's validity gate will re-check once
      // more digits arrive.
      onChange(null);
      return;
    }

    // No + present → the user is editing just the national part; keep the
    // currently-selected country.
    const nationalDigits = cleaned.replace(/\D/g, "");
    emit(iso2, nationalDigits);
  }

  const nationalDigits = extractNationalDigits(raw, iso2);
  const fullE164 = nationalDigits
    ? `${COUNTRIES.find((c) => c.iso2 === iso2)?.dialCode ?? ""}${nationalDigits}`
    : "";
  const isValid = !fullE164 ? true : isValidPhoneNumber(fullE164, iso2 as CountryCode);
  const showError = touched && !!nationalDigits && !isValid;

  return (
    <div>
      <div className="flex gap-2">
        <CountryPicker
          value={iso2}
          onChange={handleCountryChange}
          variant="phone"
          disabled={disabled}
          triggerClassName="w-[8.5rem] shrink-0"
        />
        <Input
          id={inputId}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          className="flex-1"
          value={raw}
          required={required}
          disabled={disabled}
          placeholder="+30 690 1234567"
          aria-invalid={showError || undefined}
          onChange={(e) => handleRawChange(e.target.value)}
          onBlur={() => setTouched(true)}
        />
      </div>
      {showError && (
        <p className="mt-1 text-xs text-destructive">
          That doesn't look like a valid phone number for {COUNTRIES.find((c) => c.iso2 === iso2)?.name ?? iso2}.
        </p>
      )}
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────────

function formatRaw(dial: string, national: string): string {
  const digits = national.replace(/\D/g, "");
  return digits ? `${dial} ${digits}` : `${dial} `;
}

/**
 * Best-effort extraction of the national number from the current raw string
 * given the currently-selected country. Used for validation only — the
 * change handlers keep raw + iso2 in sync themselves.
 */
function extractNationalDigits(raw: string, iso2: string): string {
  const dial = COUNTRIES.find((c) => c.iso2 === iso2)?.dialCode ?? "";
  const trimmed = raw.trimStart();
  const withoutDial = trimmed.startsWith(dial) ? trimmed.slice(dial.length) : trimmed;
  return withoutDial.replace(/\D/g, "");
}
